import uuid from "uuid";
import i18next from "i18next";

import Validator from "services/validator";
import store from "services/store";
import createFormActions from "modules/form/actions";

import {
  getRepositories,
  getSelectedRepository,
  getSelectedLayer,
  getSelectedPackVersion,
  getCurrentSidebarStep,
  getLayers,
  getPackVersions,
  getReversedLayers,
  areAllLayersConfigured,
  getSelectedVersionParent,
  getSelectedVersionValues,
  getFormattedPayload,
  getCurrentLayerIndex,
  getFilteredExpandedKeys,
  getSelectedVersionDefaultValues,
  getSelectedVersionPresets,
} from "state/clusterprofile/selectors/layerConfig";
import { trackGameProgress } from "state/game/actions";

import { Missing } from "services/validator/rules";
import notifications from "services/notifications";
import api from "services/api/index";
import ModalService from "services/modal";

import { PackVersionSchema, PackSchema } from "utils/schemas";
import { PACK_STEPS } from "utils/constants/clusterprofile";
import { parseYAMLValues, getDefaultPresetsFromValues } from "utils/parsers";

import {
  ADD_GUID,
  LAYERS,
  OS_GUID,
} from "state/clusterprofile/reducers/layerConfig";
import { getRawClusterProfile } from "state/clusterprofile/selectors/details";

import {
  repositoriesFetcher,
  packNamesFetcher,
  configurePacksModal,
} from "../services";
import { getActiveGame } from "state/game/selectors";

//

export const CLUSTER_PROFILE_MODULE = "clusterprofile";

export const validator = new Validator();
const layerValidator = new Validator();
layerValidator.addRule(["config"], Missing());

function createValidator({ params = [] } = {}) {
  validator.removeRules();
  validator.addRule(["name", "cloudType"], Missing());
  validator.addRule(["layers"], layerValidator);

  params.forEach((param) => {
    if (param.required) {
      validator.addRule(param.reference, Missing());
    }
  });
}

createValidator();

export const clusterProfileFormActions = createFormActions({
  validator,
  async init() {
    store.dispatch(fetchRepositories());
    return Promise.resolve({
      persisted: false,
      name: "",
      description: "",
      tags: [],
      layers: LAYERS,
      cloudType: "aws",
    });
  },
  async submit(formData) {
    const payload = getFormattedPayload(store.getState());
    let promise;
    const { metadata } = getRawClusterProfile(store.getState()) || {};
    if (formData.persisted) {
      payload.metadata.uid = metadata.uid;
      promise = api.put(`v1alpha1/clusterprofiles/${metadata.uid}`, payload);
    } else {
      promise = api.post("v1alpha1/clusterprofiles", payload);
    }

    try {
      const response = await promise;

      await api.patch(
        `v1alpha1/clusterprofiles/${response.uid || metadata.uid}/publish`,
      );

      notifications.open({
        message: `Cluster profile ${formData.name} saved and published`,
      });

      return promise;
    } catch (error) {
      const message = formData.persisted
        ? i18next.t("Something went wrong when editing the cluster profile")
        : i18next.t("Something went wrong when creating the cluster profile");

      notifications.open({
        message,
        description: error.message,
      });
    }
  },
});

export function resetForwardSteps() {
  return (dispatch, getState) => {
    const state = getState();
    const steps = state.clusterprofile.create?.steps || [];
    const currentStep = state.clusterprofile.create?.stepNumber;

    steps.forEach((_, index) => {
      if (index >= currentStep) {
        dispatch({
          type: "CLUSTER_PROFILE_SET_DESCRIPTION",
          stepNumber: index,
          description: "",
        });
      }
    });
  };
}

export function onTreeSearch(value) {
  return function thunk(dispatch, getState) {
    dispatch({
      type: "VERSION_TREE_SEARCH",
      treeSearchValue: value,
    });

    dispatch(setVersionExpandedKeys(getFilteredExpandedKeys(getState())));
  };
}

export function deselectPack() {
  return function thunk(dispatch, getState) {
    const selectedLayer = getSelectedLayer(getState());
    const currentStep = getCurrentSidebarStep(getState());
    const steps = PACK_STEPS[selectedLayer.type];
    const stepIndex = steps.indexOf(currentStep);
    const prevStep = steps[stepIndex - 1];

    dispatch(setPackStep(prevStep));
  };
}

export function fetchRepositories() {
  return repositoriesFetcher.fetch();
}

export function setAvailableRepositories(repositories) {
  return (dispatch) => {
    dispatch({
      type: "SET_AVAILABLE_REPOSITORIES",
      availableRepositories: repositories,
    });
  };
}

function packTagsCompare(tag1, tag2) {
  const [major1, minor1, patch1] = tag1.version
    .split(".")
    .map((versionType) => parseInt(versionType));
  const [major2, minor2, patch2] = tag2.version
    .split(".")
    .map((versionType) => parseInt(versionType));

  if (major1 < major2) {
    return 1;
  }

  if (major1 === major2 && minor1 < minor2) {
    return 1;
  }

  if (minor1 === minor2 && patch1 < patch2) {
    return 1;
  }

  return -1;
}

export function fetchPackVersions() {
  return function thunk(dispatch, getState) {
    const selectedRepository = getSelectedRepository(getState());
    const packName = getState().forms.packSteps.data.name;
    const cloudType = getState().forms.clusterprofile.data.cloudType;

    const promise = api.get(
      `v1alpha1/packs/${packName}/registries/${selectedRepository.metadata.uid}`,
      { cloudType },
    );
    dispatch({
      promise: promise.then((res) =>
        res.tags.sort(packTagsCompare).map((version) => ({
          ...version,
          pack: {
            metadata: {
              uid: version.packUid,
            },
          },
        })),
      ),
      type: "FETCH_PACK_VERSIONS",
      schema: [PackVersionSchema],
    });
    const packsPromise = promise
      .then((res) => {
        return res.packValues.map((pack) => ({
          metadata: { name: res.name, uid: pack.packUid },
          spec: pack,
        }));
      })
      .catch((err) => {
        notifications.warn({
          message: i18next.t("Unable to find the package"),
          description: i18next.t("Please go back and select another"),
        });
      });
    dispatch({
      promise: packsPromise,
      type: "FETCH_PACKS",
      schema: [PackSchema],
    });

    return packsPromise;
  };
}

export function fetchPackNames() {
  return function thunk(dispatch) {
    return dispatch(packNamesFetcher.fetch());
  };
}

const packSelectorValidator = new Validator();
packSelectorValidator.addRule(
  "name",
  Missing({ message: () => i18next.t("You need to select a pack") }),
);
packSelectorValidator.addRule(
  "version",
  Missing({ message: () => i18next.t("You need to select a pack version") }),
);

function getLayerConfig() {
  const state = store.getState();
  const layer = getSelectedLayer(state);

  let formData = {
    type: layer?.type,
    name: layer?.config?.name || "",
    version: layer?.config?.version || "",
    values: layer?.config?.values || "",
  };

  if (layer && layer.type === "k8s") {
    return Promise.resolve({
      ...formData,
      name: "kubernetes",
      type: "k8s",
    });
  }

  return Promise.resolve(formData);
}

export const packsStepFormActions = createFormActions({
  init() {
    return getLayerConfig();
  },
  submit() {
    store.dispatch(applyParams());
  },
  validator: packSelectorValidator,
});

export function selectLayer(layerGuid) {
  return async (dispatch, getState) => {
    dispatch({
      type: "SELECT_LAYER",
      selectedLayer: layerGuid,
    });

    if (!layerGuid) {
      return;
    }

    await dispatch(packsStepFormActions.init({ module: "packSteps" }));
    const layer = getSelectedLayer(getState());
    const formData = await getLayerConfig();
    dispatch(
      packsStepFormActions.batchChange({
        module: "packSteps",
        updates: formData,
      }),
    );

    if (layer.type === "new") {
      dispatch(setPackStep("type"));
      return;
    }

    // When editing a pack, go straight to version step and fetch the pack names to get pack label and icon
    const step = formData.name ? "version" : "name";

    dispatch(setPackStep(step));
  };
}

export function selectHeaderLayer(layerGuid) {
  return (dispatch) => {
    dispatch(applyParams());
    dispatch(selectLayer(layerGuid));
  };
}

export function applyParams() {
  return async (dispatch, getState) => {
    const state = getState();
    const selectedPackVersion = getSelectedPackVersion(state);
    const selectedRepository = getSelectedRepository(state);

    if (!selectedPackVersion) {
      return;
    }

    const formData = state.forms.packSteps.data;

    const selectedLayer = state.clusterprofile.layerConfig.selectedLayer;
    const layerIndex = getLayers(state).findIndex(
      (layer) => layer.guid === selectedLayer,
    );

    const config = {
      packUid: selectedPackVersion.packUid,
      name: selectedPackVersion?.pack?.metadata?.name,
      tag: selectedPackVersion.tag,
      version: selectedPackVersion.guid,
      values: formData.values,
      registryUid: selectedRepository.metadata.uid,
      presets: formData.presets,
    };

    dispatch(
      clusterProfileFormActions.onChange({
        value: config,
        name: `layers.${layerIndex}.config`,
        module: CLUSTER_PROFILE_MODULE,
      }),
    );

    await dispatch(
      clusterProfileFormActions.validateField({
        name: `layers.${layerIndex}.config`,
        module: CLUSTER_PROFILE_MODULE,
      }),
    );

    const errors = getState().forms[CLUSTER_PROFILE_MODULE].errors;
    if (errors.length === 0) {
      notifications.close("layer-validations-error");
    }
  };
}

export function addNewLayer() {
  return function thunk(dispatch) {
    dispatch({
      type: "SELECT_LAYER",
      selectedLayer: ADD_GUID,
    });
    dispatch({
      type: "SET_PACK_STEP",
      step: "type",
    });
  };
}

export function submitClusterProfile() {
  return async function thunk(dispatch) {
    dispatch(
      clusterProfileFormActions.submit({ module: CLUSTER_PROFILE_MODULE }),
    );
  };
}

export function setSelectedRepository(repository) {
  return function thunk(dispatch) {
    dispatch({
      type: "UPDATE_SELECTED_REPOSITORY",
      repository,
    });
    dispatch(
      packsStepFormActions.batchChange({
        module: "packSteps",
        updates: {
          version: undefined,
          values: undefined,
          presets: undefined,
        },
      }),
    );
    dispatch(fetchPackVersions());
  };
}

export function setPackStep(step) {
  return async function thunk(dispatch, getState) {
    dispatch({
      type: "SET_PACK_STEP",
      step,
    });
    if (step === "name") {
      dispatch(fetchPackNames());
    }

    if (step === "version") {
      dispatch(fetchPackNames());
      dispatch(fetchAvailableRepositories());
      dispatch(setPresetsOptions());

      const initialSelectedLayer = getSelectedLayer(getState());
      const repositories = getState().clusterprofile.layerConfig
        .availableRepositories;

      const selectedRepository = repositories.find(
        (repository) =>
          repository.metadata.uid === initialSelectedLayer?.config?.registryUid,
      );
      dispatch({
        type: "UPDATE_SELECTED_REPOSITORY",
        repository: selectedRepository?.guid || repositories[0]?.guid,
      });
      await dispatch(fetchPackVersions());
      const currentLayer = getSelectedLayer(getState());
      if (initialSelectedLayer?.guid !== currentLayer?.guid) {
        return;
      }

      const state = getState();
      const selectedPackVersion = getSelectedPackVersion(state);
      const packVersions = getPackVersions(state);
      const updatedPackVersion = packVersions.find((packVersion) => {
        return (
          packVersion.packUid === selectedPackVersion?.packUid &&
          selectedPackVersion?.tag === packVersion.tag
        );
      });

      if (updatedPackVersion) {
        dispatch(
          packsStepFormActions.onChange({
            name: "version",
            value: updatedPackVersion.guid,
            module: "packSteps",
          }),
        );
      }

      const selectedParentVersion = getSelectedVersionParent(getState())?.guid;
      if (selectedParentVersion) {
        dispatch(setVersionExpandedKeys([selectedParentVersion]));
      }
    }
  };
}

export function setPackValues() {
  return (dispatch, getState) => {
    const packValues = getSelectedVersionValues(getState());
    dispatch(
      packsStepFormActions.onChange({
        name: "values",
        value: packValues,
        module: "packSteps",
      }),
    );
    dispatch(applyParams());
    dispatch(trackGameProgress());
  };
}

function fetchAvailableRepositories() {
  return function (dispatch, getState) {
    const state = getState();
    const currentPackName = state.forms.packSteps.data.name;
    const packNames = packNamesFetcher.selector(state).result;
    const selectedPackName = packNames?.find(
      (pack) => pack.value === currentPackName,
    );
    if (selectedPackName) {
      const repositories = getRepositories(state).filter((repository) =>
        selectedPackName.registries.includes(repository.metadata.uid),
      );
      dispatch(setAvailableRepositories(repositories));
    } else {
      const repositories = getRepositories(state);
      dispatch(setAvailableRepositories(repositories));
    }
  };
}

function setPresetsOptions() {
  return function (dispatch, getState) {
    const state = getState();
    const presets = getSelectedVersionPresets(state);

    dispatch(
      packsStepFormActions.onChange({
        module: "packSteps",
        name: "presets",
        value: presets,
      }),
    );
  };
}

// TODO: Might need some refactoring
export function onPacksFormChange(name, value) {
  return function thunk(dispatch, getState) {
    const state = getState();
    const selectedLayer = getSelectedLayer(state);
    const currentStep = getCurrentSidebarStep(state);
    const steps = PACK_STEPS[selectedLayer.type];
    const stepIndex = steps.indexOf(currentStep);
    const nextStep = steps[stepIndex + 1];

    dispatch(
      packsStepFormActions.onChange({
        module: "packSteps",
        name,
        value,
      }),
    );

    if (name === "values") {
      dispatch(applyParams());
      return;
    }

    if (currentStep === "name") {
      dispatch(setPackName());
      if (state.forms.packSteps.data?.name !== value) {
        dispatch(
          packsStepFormActions.onChange({
            module: "packSteps",
            name: "version",
            value: null,
          }),
        );
      }
      dispatch(setPackStep(nextStep));
    }

    if (currentStep === "version") {
      dispatch(setPresetsOptions());
      dispatch(setPackValues());
    }

    if (currentStep === "type") {
      const layers = getLayers(getState());
      let newLayers = [];

      if (selectedLayer.type === "new") {
        const guid = uuid();
        newLayers = [{ guid, type: value, config: null }, ...layers];

        dispatch({
          type: "ADD_LAYER",
          guid,
          layerType: value,
        });
      } else {
        if (selectedLayer.type === value) {
          dispatch(setPackStep(nextStep));
          return;
        }

        newLayers = layers.map((layer) => {
          if (layer.guid === selectedLayer.guid) {
            return {
              guid: layer.guid,
              type: value,
              config: null,
            };
          }
          return layer;
        });

        dispatch(
          packsStepFormActions.batchChange({
            module: "packSteps",
            updates: {
              type: value,
              name: "",
              version: "",
              values: "",
            },
          }),
        );
      }

      dispatch(
        clusterProfileFormActions.onChange({
          module: CLUSTER_PROFILE_MODULE,
          name: "layers",
          value: newLayers,
        }),
      );
      dispatch(setPackStep(nextStep));
    }
  };
}

export function deleteLayer(layerGuid) {
  return function thunk(dispatch, getState) {
    const layers = getState().forms.clusterprofile.data.layers.filter(
      (layer) => layer.guid !== layerGuid,
    );
    dispatch({
      type: "FORM_ON_CHANGE",
      module: CLUSTER_PROFILE_MODULE,
      name: "layers",
      value: layers,
    });
    dispatch(setPackStep("preview"));
    dispatch(selectLayer(""));
  };
}

export function openConfigureClusterProfileModal() {
  return (dispatch, getState) => {
    dispatch(packsStepFormActions.init({ module: "packSteps" }));
    //TODO: to be removed once we get confirmation on cancel and clear all layers config, since it will always be OS layer
    const firstUnconfiguredLayer = getReversedLayers(getState()).find(
      (layer) => !layer?.config?.version,
    );
    dispatch(selectLayer(firstUnconfiguredLayer?.guid));
    configurePacksModal.open();
  };
}

export function setPackName() {
  return (dispatch, getState) => {
    const state = getState();
    const formData = state.forms.packSteps.data;

    const selectedLayer = state.clusterprofile.layerConfig.selectedLayer;
    const layerIndex = getLayers(state).findIndex(
      (layer) => layer.guid === selectedLayer,
    );

    const config = {
      name: formData?.name,
    };

    dispatch(
      clusterProfileFormActions.onChange({
        value: config,
        name: `layers.${layerIndex}.config`,
        module: CLUSTER_PROFILE_MODULE,
      }),
    );
  };
}

export function configureLayer(guid) {
  return (dispatch) => {
    dispatch(selectLayer(guid));
    configurePacksModal.open();
  };
}

export function setNextLayer() {
  return (dispatch, getState) => {
    const currentLayerIndex = getCurrentLayerIndex(getState());
    const layers = getReversedLayers(getState());

    const currentGame = getActiveGame(getState());
    if (currentLayerIndex === -1 && currentGame.label === "Mission 2") {
      dispatch(selectLayer(ADD_GUID));
      return;
    }

    if (currentLayerIndex === layers.length - 1) {
      dispatch(selectLayer(ADD_GUID));
    } else {
      const nextLayer = layers[currentLayerIndex + 1];
      dispatch(selectLayer(nextLayer.guid));
    }
  };
}

export function finishConfiguration() {
  return async (dispatch) => {
    const errors = await dispatch(
      clusterProfileFormActions.validateForm({ module: "clusterprofile" }),
    );
    if (errors.length) {
      notifications.warn({
        message: i18next.t("Configuration is invalid"),
        description: errors.map((error) => error.result).join(". "),
      });
      return;
    }
    dispatch(applyParams());
    configurePacksModal.close();
  };
}

export function selectConfiguredLayer(layerGuid) {
  return (dispatch, getState) => {
    const layers = getLayers(getState());
    const selectedLayer = layers.find((layer) => layer.guid === layerGuid);

    if (selectedLayer?.description || layerGuid === OS_GUID) {
      dispatch(selectLayer(layerGuid));
    }
  };
}

export function selectAddNewLayer() {
  return (dispatch, getState) => {
    const areLayerConfigured = areAllLayersConfigured(getState());

    if (areLayerConfigured) {
      dispatch(selectLayer(ADD_GUID));
    }
  };
}

export function seeProfilePreview() {
  return (dispatch) => {
    dispatch(applyParams());
    dispatch(setPackStep("preview"));
    dispatch(selectLayer(""));
  };
}

export function setVersionExpandedKeys(versionGuids = []) {
  return {
    type: "SET_VERSION_EXPANDED_KEYS",
    expandedKeys: versionGuids,
  };
}

export function acknowledgeEditorNotifications() {
  return function thunk(dispatch, getState) {
    const currentLayer = getSelectedLayer(getState());

    dispatch({
      type: "ACKNOWLEDGE_EDITOR_NOTIFICATIONS",
      layerGuid: currentLayer.guid,
    });
  };
}

export const revertPackValuesModalService = new ModalService("packRevert");

export function openRevertPackModal() {
  return function thunk(dispatch, getState) {
    revertPackValuesModalService.open().then(() => {
      const state = getState();
      const defaultValues = getSelectedVersionDefaultValues(state);
      dispatch(onPacksFormChange("values", defaultValues));
      dispatch(resetPresetsValues());
    });
  };
}

export function resetPresetsValues() {
  return function thunk(dispatch, getState) {
    const packVersion = getSelectedPackVersion(getState());
    const defaultOptions = getDefaultPresetsFromValues(
      packVersion?.pack?.spec?.values,
    );

    dispatch(
      packsStepFormActions.onChange({
        module: "packSteps",
        name: "presets",
        value: defaultOptions,
      }),
    );
  };
}

export function onPresetsChange({ value, group, presets }) {
  return function thunk(dispatch, getState) {
    const state = getState();
    const packSteps = state.forms.packSteps?.data;
    const selectedPresets = presets.find(
      (preset) => preset.group === group && preset.value === value,
    );
    const updatedValues =
      parseYAMLValues(packSteps.values, selectedPresets) || packSteps.values;
    const updatedPresets = {
      ...(packSteps.presets || {}),
      [group]: value,
    };

    dispatch(
      packsStepFormActions.batchChange({
        module: "packSteps",
        updates: {
          presets: updatedPresets,
          values: updatedValues,
        },
      }),
    );
    dispatch(applyParams());
  };
}

export function onEditorMarkersUpdated(markers) {
  return function thunk(dispatch, getState) {
    const currentLayer = getSelectedLayer(getState());
    const layers = getLayers(getState());
    const selectedIndex = layers.findIndex(
      (layer) => layer.guid === currentLayer.guid,
    );

    dispatch({
      type: "UPDATE_FORM_ERRORS",
      module: "clusterprofile",
      errors: [
        {
          guid: "MARKER_ERROR",
          field: `layers.${selectedIndex}.config.values`,
          result: markers.length
            ? i18next.t("Layer {{layerName}} has invalid yaml configuration", {
                layerName: currentLayer.name,
              })
            : false,
        },
      ],
    });
  };
}
